import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Optional,
  Self,
  inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { QUANTITY_DECIMALS, normaliseQty } from '../utils/quantity.util';

/**
 * Quantity box that only accepts what the SKU can actually be counted in.
 *
 *   <input type="number" appQtyInput [allowDecimal]="allowsDecimalQty(item)"
 *          [(ngModel)]="item.receivedQuantity" />
 *
 * Counted SKUs (PCS, NOS, PKT …): digits only — the decimal point, comma, `e`
 * and the sign keys are refused, and a pasted "2.5" is rounded on the spot.
 * Measured SKUs (kg, gm, ltr, bulk, weighable): up to three decimals, the
 * precision the DECIMAL(18,3) columns store.
 *
 * `step` and `inputmode` are set to match, which also stops the browser's own
 * spinner from snapping 2.5 to a whole number on a weighable line and puts the
 * right keypad in front of a warehouse tablet.
 *
 * This is the courtesy layer. The server applies the same rule on save
 * (QuantityRulesService), so an old build or a direct API call is still held to
 * it.
 */
@Directive({ selector: 'input[appQtyInput]', standalone: true })
export class QtyInputDirective implements OnChanges {
  private el = inject(ElementRef<HTMLInputElement>);

  /** True for bulk / weighable / kg-gm-ltr lines. */
  @Input() allowDecimal = false;
  /** Places allowed when decimals are on. */
  @Input() qtyDecimals = QUANTITY_DECIMALS;

  constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {}

  ngOnChanges(): void {
    const input = this.el.nativeElement;
    input.step = this.allowDecimal ? String(1 / Math.pow(10, this.qtyDecimals)) : '1';
    input.inputMode = this.allowDecimal ? 'decimal' : 'numeric';
    // A line that switches to a counted SKU must not keep a stale fraction.
    if (!this.allowDecimal) this.normalise();
  }

  /** Refuse the keys that would build an illegal quantity. */
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length > 1) return; // Tab, Backspace, arrows, Enter …

    // Exponent and sign never belong in a document quantity.
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
      return;
    }

    const isSeparator = event.key === '.' || event.key === ',';
    if (isSeparator) {
      if (!this.allowDecimal) {
        event.preventDefault();
        return;
      }
      // One separator per value.
      if (this.el.nativeElement.value.includes('.')) event.preventDefault();
      return;
    }

    if (!/[0-9]/.test(event.key)) event.preventDefault();
  }

  /** Catches paste, drag-drop and the spinner. */
  @HostListener('input')
  onInput(): void {
    if (this.allowDecimal) return; // trimmed to precision on blur
    const raw = this.el.nativeElement.value;
    if (raw === '' || /^\d+$/.test(raw)) return;
    this.normalise();
  }

  @HostListener('blur')
  onBlur(): void {
    this.normalise();
  }

  /**
   * Writes the corrected value back through ngModel/formControl so the
   * component's own model — which is what gets saved — agrees with the box.
   */
  private normalise(): void {
    const input = this.el.nativeElement;
    const raw = input.value;
    if (raw === '' || raw == null) return;

    const cleaned = normaliseQty(raw.replace(',', '.'), this.allowDecimal);
    const asText = String(cleaned);
    if (asText === raw) return;

    input.value = asText;
    const control = this.ngControl?.control;
    if (control) {
      control.setValue(cleaned, { emitEvent: true });
    } else {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
